// @ts-strict-ignore
import { WireContourService } from '@3d-scene/common/util/wire-contour-service';
import * as JSZip from 'jszip';
import * as JSZipUtils from 'jszip-utils';
import { DDSLoader } from 'three/examples/jsm/loaders/DDSLoader.js';
import { MTLLoader } from '@3d-scene/loaders/MTLLoader';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader.js';
import { GLTF, GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { firstValueFrom, forkJoin, from, Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { ArticleVO } from '../model/vo/article.vo';
import { ArticleModelDataHolder, EmptyArticleModelData, ParsedArticleModelData } from '@3d-scene/model3d/cache/model-data';
import { StringUtils } from '@shared/util/string-utils';
import { MeshUtils } from '@3d-scene/common/util/mesh-utils';
import { FaceUtils } from '@3d-scene/common/util/face-utils';
import { TextureUtils } from '@3d-scene/model3d/util/texture-utils';
import { RepresentationVO } from '@shared/vo/representation.vo';
import { LocaleUtils } from '@shared/util/locale-utils';
import { ArticleDataService } from './article-data.service';
import { Log } from '@shared/debug/log';
import { ProjectModel } from '@project/model/project.model';
import { NumberUtils } from '@shared/util/number-utils';
import { Object3DUtils } from '@3d-scene/common/util/object-3d-utils';
import { Injectable } from '@angular/core';
import { ConfiguratorParameters } from '@3d-scene/configurator-parameters/configurator-parameters';
import { IsThreeJsType } from '@3d-scene/common/util/is-threejs-type';
import { Color, DoubleSide, Group, LoadingManager, Material, ObjectLoader } from 'three';
import { FileUtils } from '@shared/util/file-utils';

const LOADER_OBJ: string = '.obj';
const LOADER_OBZ: string = '.obz';
const LOADER_ZIP: string = '.zip';
const LOADER_JSON: string = '.json';

@Injectable({ providedIn: 'root' })
export class ModelLoadingService {

  constructor(private articleDataService: ArticleDataService, private projectModel: ProjectModel) { }

  private getURL(url: string): string {
    if (this.projectModel.isAdminView) {
      return url + '?no-cache=' + new Date().getTime();
    } else {
      return url;
    }
  }

  public async loadModel(article: ArticleVO, isArticleRepresentationsEnabled: boolean): Promise<ArticleModelDataHolder> {
    if (!article) {
      throw new Error('Article must be provided!');
    }

    let articleRepresentationWasUsed = false;
    let useRep: RepresentationVO = null;
    if (isArticleRepresentationsEnabled) {
      for (const rep of article.representations) {
        if (rep.type === RepresentationVO.TYPE_ARTICLE) {
          useRep = rep;
          articleRepresentationWasUsed = true;
          break;
        }
      }
    }
    if (!useRep) {
      for (const rep of article.representations) {
        if (rep.type === RepresentationVO.TYPE_VIEW3D) {
          useRep = rep;
          break;
        }
      }
    }
    if (useRep) {
      let url: string = LocaleUtils.getValue(useRep.url, LocaleUtils.DEFAULT_LOCALE);
      if (url.charAt(0) !== '/') {
        url = '/' + url;
      }
      const parserType: string = url.substr(url.length - 4);
      // const sourceUrl: string = url;
      // if (!mainModel.cacheEnabled) url = url + "?nocache=" + Math.random();
      let type: string = parserType;
      if (!type) {
        type = StringUtils.getFileExtension(url);
      }

      let object: any;
      try {
        object = await this.load(url, type);
      } catch (error) {
        Log.error('Could not load article model: ', error);
        throw error;
      }

      const articleModelDataHolder: ArticleModelDataHolder = new ArticleModelDataHolder(article, object, type, url);
      const articleModelData: ParsedArticleModelData | EmptyArticleModelData = articleModelDataHolder.modelData;
      if (articleModelData instanceof ParsedArticleModelData) {
        FaceUtils.reorderUVs(articleModelData.modelData as Group);
      }

      if (articleRepresentationWasUsed) {
        const articleVO: ArticleVO = articleModelDataHolder.article;
        const defaultArticleRepresentationName: string = ConfiguratorParameters.defaultArticleRepresentationName;

        const representationArticle: ArticleVO = await firstValueFrom(
          this.articleDataService.loadRepresentationArticle(articleVO.identity, defaultArticleRepresentationName)
        );
        articleModelDataHolder.article.metaElements = representationArticle.metaElements;
      }
      return articleModelDataHolder;
    }
  }

  private async load(url:string, type: string): Promise<any> {
    switch (type) {
      case LOADER_OBJ:
        return this.loadObj(url);
      case LOADER_OBZ:
        return this.loadZip(url);
      case LOADER_ZIP:
        return this.loadZip(url);
      case LOADER_JSON:
        return this.loadJson(url);
      default:
        throw new Error('Unknown loader file type: ' + type);
    }
  }

  private loadJson(url: string): Promise<any> {
    const objectLoader = new ObjectLoader();
    return new Promise((resolve, reject) => objectLoader.load(this.getURL(url), (object)=> resolve(object), undefined, (err) => reject(err)));
  }

  private async loadObj(url: string): Promise<ParsedArticleModelData> {
    const component: ModelLoadingService = this;
    const manager = new LoadingManager();
    manager.addHandler(/\.dds$/i, new DDSLoader());
    const mtlLoader = new MTLLoader();
    mtlLoader.setResourcePath(url.substring(0, url.lastIndexOf('/') + 1));
    url = url.substring(0, url.lastIndexOf('.'));

    let materials;
    try {
      materials = await new Promise((resolve, reject) =>
        mtlLoader.load(this.getURL(url + '.mtl'),(materials) => resolve(materials), undefined, (err)=> reject(err))
      )
    } catch (error) {
      Log.error('Could not load material: ', error);
      throw error;
    }

    materials.preload();

    const objLoader = new OBJLoader();
    objLoader.setMaterials(materials);

    return new Promise((resolve, reject) => {
      objLoader.load(url + '.obj', function (object) {
        const meshes = [];

        object.traverse(function (obj) {
          if (IsThreeJsType.mesh(obj)) {
            meshes.push(obj);
            if ((obj.material as Material).name) {
              let material;
              if (MeshUtils.isConnection(obj.name)) {
                // set to connection new material instance from materials
                material = TextureUtils.createMeshPhongMaterial(materials.create((obj.material as Material).name));
              } else {
                // set to mesh material from materials or create new
                material = materials.create((obj.material as Material).name);
              }
              if (material) {
                // set shineness because camera emits light
                if (IsThreeJsType.meshPhongMaterial(material)) {
                  material.shininess = 0;
                  material.needsUpdate = true;
                }

                obj.material = material;
              }
            }
          }
        });
        component.removeInitialWirecontour(object);
        const name: string = url.substring(url.lastIndexOf('/') + 1, url.length);
        const parsedArticleModelData: ParsedArticleModelData = new ParsedArticleModelData(object, name);
        resolve(parsedArticleModelData);
      }, undefined, (err) => reject(err));
    })
  }

  private async loadZip(url: string): Promise<ParsedArticleModelData> {
    const component: ModelLoadingService = this;
    let data;
    try {
      data = await new Promise((resolve, reject) => {
        JSZipUtils.getBinaryContent(this.getURL(url), (err, data) => {
          if (err) {
            Log.info('Could not load binary content');
            reject(err);
            return;
          }
          resolve(data)
        }, undefined, (err) => reject(err));
    });
    } catch (error) {
      Log.info(error);
      return;
    }
    return component.parseModelFromZipFile(data, component);
  }

  public async parseModelFromZipFile(file: File, serviceComponent?: ModelLoadingService): Promise<ParsedArticleModelData> {
    const component: ModelLoadingService = serviceComponent || this;

    const files = await FileUtils.unzipFileSecured(file);
    let objFile;
    let mtlFile;
    let imageFiles = [];

    // Iterate through the map to find MTL, OBJ, and image files
    for (const file of files) {
      if (/(.mtl$|.MTL$)/.test(file.name)) {
        mtlFile = file;
      } else if (/(.obj$|.OBJ$)/.test(file.name)) {
        objFile = file;
      } else if (/(.png$|.PNG$|.jpg$|.JPG$|.jpeg$|.svg$)/.test(file.name)) {
        imageFiles.push(file);
      }
    }



    const objLoader = new OBJLoader();

    const parsedArticleModelData: ParsedArticleModelData = new ParsedArticleModelData();
    parsedArticleModelData.name = objFile ? objFile.name : '';

    if (mtlFile && objFile) {
      const mtlLoader: MTLLoader = new MTLLoader();
      mtlLoader.setResourcePath(MTLLoader.ZIP_BASE_URL);
      let mtlContent = await mtlFile.async('string')
      let materialsCreator :any = mtlLoader.parse(mtlContent, MTLLoader.ZIP_BASE_URL);

      objLoader.setMaterials(materialsCreator);
      let objContent = await objFile.async('string')
      const object = objLoader.parse(objContent);
      object.name = objFile.name;

      let textureImages = [];
      if (imageFiles.length > 0) {
        textureImages = await firstValueFrom(component.getImagesFromFiles(imageFiles));
      }

      const updateMaterials = (object3d) => {
        component.updateMeshMaterial(object3d, materialsCreator, textureImages);
        if (object3d.children) {
          object3d.children.forEach(ch => updateMaterials(ch));
        }
      };
      object.traverse((object3d) => updateMaterials(object3d));

      component.removeInitialWirecontour(object);
      parsedArticleModelData.modelData = object;
      return parsedArticleModelData;
    } else if (objFile) {
      let objContent = await objFile.async('string')
      const object = objLoader.parse(objContent);

      component.removeInitialWirecontour(object);
      parsedArticleModelData.modelData = object;
      return parsedArticleModelData;
    } else {
      throw new Error('No files found.');
    }
  }

  public parseModelFromGlbFileAsync(file: File): Promise<ParsedArticleModelData> {
    return new Promise((resolve, reject) => {
      this.parseModelFromGlbFile(file, (parsedArticleModelData: ParsedArticleModelData) => {
        resolve(parsedArticleModelData);
      }, (error) => {
        Log.error('Could not parse GLB file: ', error);
        reject(error);
      });
    });
  }

  public parseModelFromGlbFile(file: File, onload, onerror?): Observable<boolean> {
    const resultSubject: Subject<boolean> = new Subject();
    const showError = (error?: any) => {
      if (onerror) {
        onerror();
      }
      resultSubject.next(false);
    };
    const manager = new LoadingManager();
    manager.addHandler(/\.dds$/i, new DDSLoader());
    const gltfLoader: GLTFLoader = new GLTFLoader();
    const reader: FileReader = new FileReader();
    reader.onloadend = (event) => {
      gltfLoader.parse(reader.result, '', (gltf: GLTF) => {
        const name: string = file.name;
        const object: Group = new Group();
        gltf.scene.children.forEach(ch => {
          if (IsThreeJsType.group(ch) || (IsThreeJsType.object3D(ch) && ch.type === 'Object3D') || IsThreeJsType.mesh(ch)) {
            Object3DUtils.moveTransformationToGeometry(ch);
            object.add(ch);
          }
        });
        if (object) {
          object.name = name;
          const updateMaterials = (object3d) => {
            this.updateMeshMaterial(object3d, null, []);
            if (object3d.children) {
              object3d.children.forEach(ch => updateMaterials(ch));
            }
          };
          object.traverse((object3d) => updateMaterials(object3d));
          const parsedArticleModelData: ParsedArticleModelData = new ParsedArticleModelData(object, name);
          onload(parsedArticleModelData);
          resultSubject.next(true);
        } else {
          showError();
        }
      }, (e: any) => showError(e));
    };
    reader.onerror = (error) => showError(error);
    reader.readAsArrayBuffer(file);
    return resultSubject;
  }

  private updateMeshMaterial(object3d: any, materialCreator?: MTLLoader.MaterialCreator, textures: HTMLImageElement[] = []): void {
    if (!object3d) {
      return;
    }

    if (IsThreeJsType.mesh(object3d) || IsThreeJsType.lineSegments(object3d)) {
      // supporting of multiple materials
      const materials: Material[] = Array.isArray(object3d.material) ? object3d.material : [object3d.material];

      for (let i = 0; i < materials.length; i++) {
        let material: any = materials[i] as Material;
        let newMaterial: Material;
        if (materialCreator && material && material.name) {
          if (MeshUtils.isConnection(object3d.name)) {
            // set to connection new material instance from materials
            newMaterial = TextureUtils.createMeshPhongMaterial(materialCreator.create((material as Material).name));
          } else {
            // set to mesh material from materials or create new
            newMaterial = materialCreator.create((material as Material).name);
          }
        } else if (IsThreeJsType.meshStandardMaterial(material)) {
          const meshPhongMaterial = TextureUtils.createMeshPhongMaterial(material);
          meshPhongMaterial.color = new Color(this.calculateHexColorFromGLTFBaseColorFactor(meshPhongMaterial.color));
          newMaterial = meshPhongMaterial;
        }
        if (newMaterial) {
          materials[i] = newMaterial;
          material = newMaterial;
        }

        if (material && material.map) {
          if (material.map.image) {
            const arr = material.map.image.src.split('/');
            let fImg = textures.find(img => img.name === material.name);
            if (fImg) {
              material.map.image.src = fImg.src;
              material.map.name = fImg.name;
              material.map.needsUpdate = true;
            } else {
              fImg = textures.find(img => arr[arr.length - 1].indexOf(img.name) !== -1);
              if (fImg) {
                material.map.image.src = fImg.src;
                material.map.name = fImg.name;
                material.map.needsUpdate = true;
              }
            }
            material.map.anisotropy = ConfiguratorParameters.maxRendererAnisotropy;
            // set transparent background
            material.transparent = true;
            material.opacity = 1;
            // update properties for support transparent images
            material.alphaTest = 0.5;
            material.side = DoubleSide;
            material.needsUpdate = true;
          }
        }
        if (material) {
          // set shineness because camera emits light
          if (IsThreeJsType.meshPhongMaterial(material)) {
            material.shininess = 2;
            material.needsUpdate = true;
          }
          // ignore depth buffer for transparent materials without texture to fix rendering order of transparent objects
          if (material.transparent && !material.map) {
            material.depthWrite = false;
          }
        }
      }
      object3d.material = materials && materials.length === 1 ? materials[0] : materials;
    }
  }

  private calculateHexColorFromGLTFBaseColorFactor(colorValue: Color): string {
    const getMaterialComponent = (component: number) => {
      return parseInt(Math.round(component * 255.0).toString(), 10);
    };

    const resultColor: Color = colorValue.convertLinearToSRGB();
    resultColor.r = getMaterialComponent(resultColor.r);
    resultColor.g = getMaterialComponent(resultColor.g);
    resultColor.b = getMaterialComponent(resultColor.b);

    const r: string = NumberUtils.integerToHex(resultColor.r);
    const g: string = NumberUtils.integerToHex(resultColor.g);
    const b: string = NumberUtils.integerToHex(resultColor.b);

    return `#${r + g + b}`;
  }

  private getImagesFromFiles(files: JSZip.JSZipObject[]): Observable<HTMLImageElement[]> {
    const observables = [];
    for (let i = 0; i < files.length; i++) {
      observables.push(from(files[i].async('base64')));
    }

    return forkJoin(observables).pipe(map((imgContents) => {
      const images = [];
      for (let i = 0; i < imgContents.length; i++) {
        const img = new Image();
        img.src = `data:image/${files[i].name.split('.')[1]};base64,${imgContents[i]}`;
        img.name = files[i].name.split('.')[0];
        images.push(img);
      }
      return images;
    }));
  }

  // remove initial wirecontour of article if exist (later creates new)
  private removeInitialWirecontour(group: Group): void {
    group.children.forEach((child) => {
      if (IsThreeJsType.lineSegments(child) && child.name === WireContourService.WIRECONTOUR_NAME) {
        group.remove(child);
      }
    });
  }
}
